package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Flashcard
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.core.CoreCarte
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis
import org.teddapp.project.ui.TeddDates

/** Cartes de révision, rangées dans les six cases de Leitner. */
class FlashcardRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Flashcard>> =
        db.flashcardQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        front: String,
        back: String,
        skillId: String? = null,
        note: String? = null,
        orderIndex: Long = 0,
        id: String = newId("fc"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        // Une carte neuve est à réviser tout de suite : case 1, échéance aujourd'hui.
        db.flashcardQueries.insert(
            id, skillId, front.trim(), back.trim(), note?.takeIf { it.isNotBlank() },
            1L, TODAY_ISO, null, 0L, 0L, orderIndex, now, now,
        )
        id
    }

    suspend fun update(id: String, skillId: String?, front: String, back: String, note: String?) =
        withContext(Dispatchers.Default) {
            db.flashcardQueries.update(skillId, front.trim(), back.trim(), note?.takeIf { it.isNotBlank() }, nowMillis(), id)
        }

    /**
     * Enregistre une révision et range la carte là où le noyau le dit.
     *
     * La case et le nombre de jours ne sont pas calculés ici : le fait part au noyau,
     * qui applique la boîte de Leitner et rend l'état de la carte. Sans noyau, la
     * révision n'est pas planifiable — la fonction renvoie alors faux et la carte
     * reste où elle est, plutôt que d'inventer une seconde règle.
     */
    suspend fun review(
        card: Flashcard,
        remembered: Boolean,
        core: CoreSession?,
        today: String = TODAY_ISO,
    ): CoreCarte? = withContext(Dispatchers.Default) {
        val session = core ?: return@withContext null
        val etat = session.recordAndRead(
            TeddIdentity.USER_ID,
            CoreFacts.flashcardReviewed(card.id, card.skill_id, remembered),
        )?.revisions?.get(card.id) ?: return@withContext null

        db.flashcardQueries.review(
            etat.case.toLong(),
            // Le délai vient du noyau ; l'addition de jours n'est que du calendrier.
            TeddDates.addDays(today, etat.intervalleJours),
            today,
            etat.revisions,
            etat.oublis,
            nowMillis(),
            card.id,
        )
        etat
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.flashcardQueries.softDelete(now, now, id)
    }
}
